import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import ImageUpload from '@/components/ImageUpload';

export default function TCGManagement() {
  const [tcgs, setTcgs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', image_url: '' });

  const load = () => api.get('/admin/tcgs').then(r => setTcgs(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', description: '', image_url: '' }); setDialogOpen(true); };
  const openEdit = (tcg) => { setEditing(tcg); setForm({ name: tcg.name, description: tcg.description || '', image_url: tcg.image_url || '' }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name) { toast.error("Le nom est requis"); return; }
    try {
      if (editing) {
        await api.put(`/admin/tcgs/${editing.id}`, form);
        toast.success("TCG mis à jour");
      } else {
        await api.post('/admin/tcgs', form);
        toast.success("TCG créé");
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (tcg) => {
    if (!window.confirm(`Supprimer "${tcg.name}" ?`)) return;
    try { await api.delete(`/admin/tcgs/${tcg.id}`); toast.success("TCG supprimé"); load(); }
    catch (err) { toast.error("Erreur lors de la suppression"); }
  };

  return (
    <div data-testid="tcg-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Gestion des TCG</h1>
          <p className="text-slate-500 mt-1">Gérez les jeux de cartes à collectionner</p>
        </div>
        <Button className="rounded-full" onClick={openCreate} data-testid="create-tcg-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau TCG
        </Button>
      </div>

      {tcgs.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <Layers className="h-12 w-12 mx-auto text-slate-200" />
          <p className="mt-4 text-slate-500">Aucun TCG créé</p>
          <Button className="mt-4 rounded-full" onClick={openCreate}>Créer un TCG</Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tcgs.map(tcg => (
                <TableRow key={tcg.id} data-testid={`tcg-row-${tcg.id}`}>
                  <TableCell className="font-medium">{tcg.name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{tcg.description || '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(tcg)} data-testid={`edit-tcg-${tcg.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:text-rose-600 hover:bg-rose-50" onClick={() => handleDelete(tcg)} data-testid={`delete-tcg-${tcg.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="tcg-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le TCG' : 'Nouveau TCG'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs font-medium text-slate-500">Nom</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg" data-testid="tcg-name-input" /></div>
            <div><Label className="text-xs font-medium text-slate-500">Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-lg" data-testid="tcg-desc-input" /></div>
            <ImageUpload value={form.image_url} onChange={v => setForm(f => ({ ...f, image_url: v }))} label="Logo / visuel du TCG" testId="tcg-image" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={handleSave} className="rounded-full" data-testid="tcg-save-btn">{editing ? 'Mettre à jour' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
